import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useDialogFocus } from '@shared/hooks/useDialogFocus';
import { useAuth } from '@shared/auth/AuthContext';
import { Button, Input } from '@shared/ui';
import { captureError } from '@shared/lib/observability';

// "Post a campaign" — a business publishes an open job for creators to apply to.
// Deliberately minimal: a banner image + the few things a creator needs to decide.
// Everything else (targeting, usage rights, structured deliverables, tech specs)
// was moved out — those columns keep their DB defaults. What used to be structured
// "deliverables" now lives as free text in Requirements (как в реальных заданиях).

interface Cat { id: string; name_ru: string | null; name_en: string | null }

const BANNER_BUCKET = 'campaign-banners';
const MAX_BANNER_BYTES = 5 * 1024 * 1024;

const inputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(15,45,100,0.5)',
  background: 'rgba(255,255,255,0.04)', color: '#eee', fontSize: 'var(--fs-body)', minHeight: 40,
  // minWidth:0 lets the native date/number inputs shrink inside a grid/flex cell —
  // without it their intrinsic min-width (esp. iOS date picker) forces the whole
  // modal wider than the viewport and the right edge gets clipped on mobile.
  outline: 'none', width: '100%', minWidth: 0, boxSizing: 'border-box', fontFamily: 'inherit',
};

export default function CampaignForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('customer');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const { user } = useAuth();
  const invalidate = useInvalidateSupabase();
  const fileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isBarter, setIsBarter] = useState(false);
  const [budget, setBudget] = useState('');
  const [maxCreators, setMaxCreators] = useState('3');
  const [applyDeadline, setApplyDeadline] = useState('');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState('');

  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [focusY, setFocusY] = useState(50); // vertical crop focal point (0=top..100=bottom)
  const [uploading, setUploading] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startClientY: number; startFocus: number } | null>(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dialogRef = useDialogFocus<HTMLDivElement>();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Which field the validation error belongs to — shown under that field, which gets focus.
  const [fieldMsg, setFieldMsg] = useState<string | null>(null);
  const [errField, setErrField] = useState<'title' | 'budget' | 'max' | 'deadline' | null>(null);
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

  // customer's advertiser (from org scope, ADR-001) + its brands (shown on the card)
  const { data: prof } = useSupabaseQuery<{ advertiser_id: string | null } | null>(
    ['profile-adv-cf', user?.id],
    async () => {
      if (!user) return { data: null, error: null };
      const res = await supabase.rpc('get_my_advertiser');
      const row = Array.isArray(res.data) ? res.data[0] : res.data;
      return { data: (row as { advertiser_id: string } | undefined) ?? null, error: res.error };
    },
    { enabled: !!user },
  );
  // advertiser_id links the campaign to the poster's company automatically (ADR-001).
  // We no longer ask for a brand here — the brands an organization follows live on the «Ваши бренды» panel (OnboardBrand),
  // and the banner + title identify the posting in the feed.
  const advertiserId = prof?.advertiser_id ?? null;
  const { data: cats } = useSupabaseQuery<Cat[]>(['cats-cf'], async () =>
    supabase.from('categories').select('id, name_ru, name_en').eq('is_active', true).order('sort_order'));

  const pickCover = async (file: File) => {
    setErr(null);
    if (!file.type.startsWith('image/')) { setErr(tr('campaign.err.image', 'Загрузите картинку (JPG, PNG или WebP).')); return; }
    if (file.size > MAX_BANNER_BYTES) { setErr(tr('campaign.err.imageSize', 'Картинка слишком большая (макс. 5 МБ).')); return; }
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const path = `${user?.id ?? 'anon'}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from(BANNER_BUCKET).upload(path, file, {
        contentType: file.type || 'image/jpeg', upsert: false,
      });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from(BANNER_BUCKET).getPublicUrl(path);
      setCoverUrl(data.publicUrl);
      setFocusY(50);
    } catch (e) {
      captureError(e, { where: 'campaign.cover.upload' });
      setErr(tr('campaign.err.upload', 'Не удалось загрузить картинку. Попробуйте ещё раз.'));
    } finally {
      setUploading(false);
    }
  };

  // Drag the banner up/down inside the frame to pick which slice stays visible.
  // Maps pointer travel to a 0..100 object-position; drag up reveals lower part.
  const onDragStart = (clientY: number) => { dragRef.current = { startClientY: clientY, startFocus: focusY }; };
  const onDragMove = (clientY: number) => {
    const d = dragRef.current; if (!d) return;
    const h = frameRef.current?.offsetHeight ?? 1;
    const next = d.startFocus - ((clientY - d.startClientY) / h) * 100;
    setFocusY(Math.round(Math.max(0, Math.min(100, next))));
  };
  const onDragEnd = () => { dragRef.current = null; };

  const validate = (): { field: 'title' | 'budget' | 'max' | 'deadline'; msg: string } | null => {
    if (!title.trim()) return { field: 'title', msg: tr('campaign.err.title', 'Укажите название задания.') };
    if (!isBarter && (!budget || Number(budget) <= 0)) return { field: 'budget', msg: tr('campaign.err.budget', 'Укажите бюджет (или отметьте «Бартер»).') };
    if (!maxCreators || Number(maxCreators) < 1) return { field: 'max', msg: tr('campaign.err.max', 'Сколько блогеров нужно?') };
    if (!applyDeadline) return { field: 'deadline', msg: tr('campaign.err.deadline', 'Укажите, до какой даты задание актуально.') };
    if (applyDeadline < today) return { field: 'deadline', msg: tr('campaign.err.deadlinePast', 'Дата уже прошла — выберите сегодня или позже.') };
    return null;
  };

  const save = async (publish: boolean) => {
    const v = validate();
    if (v) {
      setErr(null); setErrField(v.field); setFieldMsg(v.msg);
      // The button sits under a long form on a phone: take the person to the field.
      const el = document.getElementById(`cf-${v.field}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as HTMLInputElement | null)?.focus({ preventScroll: true });
      return;
    }
    setErrField(null); setFieldMsg(null);
    setBusy(true); setErr(null);
    const payload = {
      advertiser_id: advertiserId,
      title: title.trim(),
      category_id: categoryId || null,
      compensation_type: isBarter ? 'barter' : 'paid',
      budget: isBarter ? null : Number(budget),
      currency: 'KGS',
      max_creators: Number(maxCreators),
      apply_deadline: applyDeadline,
      cover_image_url: coverUrl,
      cover_focus_y: coverUrl ? focusY : 50,
      description: description.trim() || null,
      requirements: requirements.trim() || null,
    };
    const { data: created, error: e1 } = await supabase
      .from('creator_campaigns').insert(payload).select('id').single();
    if (e1 || !created) { setBusy(false); captureError(e1, { where: 'campaign.create' }); setErr(e1?.message ?? 'insert failed'); return; }
    const campaignId = (created as { id: string }).id;

    if (publish) {
      const { error: e3 } = await supabase.rpc('publish_campaign', { p_id: campaignId });
      if (e3) { setBusy(false); captureError(e3, { where: 'campaign.publish' }); setErr(e3.message); return; }
      // fire-and-forget Web Push to matching creators
      supabase.functions.invoke('send-campaign-push', { body: { campaign_id: campaignId } }).catch(() => {});
    }
    setBusy(false);
    invalidate(['my-campaigns']);
    onSaved();
  };

  const label = (s: string) => <div style={{ fontSize: 'var(--fs-body)', color: '#bbb', marginBottom: 4 }}>{s}</div>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '30px 16px' }}
      onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={tr('campaign.newTitle', 'Разместить задание')} onClick={(e) => e.stopPropagation()}
        style={{ outline: 'none', background: '#0d1017', border: '1px solid rgba(15,45,100,0.5)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 520, minWidth: 0, boxSizing: 'border-box', display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="c-page-title" style={{ margin: 0 }}>{tr('campaign.newTitle', 'Разместить задание')}</h2>
          <button onClick={onClose} aria-label={tr('campaign.close', 'Закрыть')} style={{ background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer', lineHeight: 1, minWidth: 44, minHeight: 44 }}>×</button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 13, margin: '-8px 0 0' }}>
          {tr('campaign.newSub', 'Коротко опишите, что нужно снять. Блогеры откликнутся — детали обсудите в переписке.')}
        </p>

        {/* Banner image — makes each posting recognisable in the feed */}
        <div>
          {label(tr('campaign.cover', 'Картинка задания'))}
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickCover(f); e.target.value = ''; }} />
          {coverUrl ? (
            <>
              {/* Crop preview = the feed banner box (fixed 130px strip, object-fit
                  cover). Drag up/down to set the vertical focal point creators see. */}
              <div ref={frameRef}
                onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); onDragStart(e.clientY); }}
                onPointerMove={(e) => { if (dragRef.current) onDragMove(e.clientY); }}
                onPointerUp={onDragEnd} onPointerCancel={onDragEnd}
                style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.18)', height: 130, background: '#000', cursor: dragRef.current ? 'grabbing' : 'grab', touchAction: 'none' }}>
                <img src={coverUrl} alt="" draggable={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `50% ${focusY}%`, display: 'block', userSelect: 'none', pointerEvents: 'none' }} />
                <button onClick={() => { setCoverUrl(null); setFocusY(50); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                  {tr('campaign.coverRemove', 'Убрать')}
                </button>
                <div style={{ position: 'absolute', left: 8, bottom: 8, background: 'rgba(0,0,0,0.55)', color: '#dfe6f0', borderRadius: 8, padding: '3px 8px', fontSize: 11, pointerEvents: 'none' }}>
                  ↕ {tr('campaign.coverDrag', 'Тяните, чтобы выбрать кадр')}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
                {tr('campaign.coverHint', 'Так карточку увидят блогеры в ленте. Важное держите в кадре — края обрезаются.')}
              </div>
            </>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ width: '100%', minHeight: 96, borderRadius: 10, cursor: uploading ? 'default' : 'pointer', fontFamily: 'inherit',
                border: '1px dashed rgba(74,158,255,0.5)', background: 'rgba(74,158,255,0.04)', color: '#6699ff', fontSize: 13 }}>
              {uploading ? tr('campaign.coverUploading', 'Загружаем…') : tr('campaign.coverAdd', '+ Добавить картинку (необязательно)')}
            </button>
          )}
        </div>

        <Input id="cf-title" label={tr('campaign.title', 'Название задания *')} value={title} onChange={(e) => { setTitle(e.target.value); if (errField === 'title') setErrField(null); }} placeholder="Лазерная эпиляция" error={errField === 'title' ? fieldMsg : null} aria-invalid={errField === 'title' || undefined} />

        <div>
          {label(tr('campaign.category', 'Категория'))}
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={inputStyle}>
            <option value="">—</option>
            {(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name_ru || c.name_en}</option>)}
          </select>
        </div>

        {/* Compensation — barter toggle hides the budget field */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#bcc', fontSize: 14, cursor: 'pointer', marginBottom: isBarter ? 0 : 8 }}>
            <input type="checkbox" checked={isBarter} onChange={(e) => setIsBarter(e.target.checked)} />
            {tr('campaign.barterToggle', 'Бартер (без оплаты деньгами)')}
          </label>
          {!isBarter && (
            <Input id="cf-budget" label={tr('campaign.budget', 'Бюджет, сом *')} value={budget} inputMode="numeric" onChange={(e) => { setBudget(e.target.value); if (errField === 'budget') setErrField(null); }} placeholder="5000" error={errField === 'budget' ? fieldMsg : null} aria-invalid={errField === 'budget' || undefined} />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            {label(tr('campaign.applyBy', 'Актуально до *'))}
            <input id="cf-deadline" type="date" min={today} value={applyDeadline} aria-invalid={errField === 'deadline' || undefined}
              onChange={(e) => { setApplyDeadline(e.target.value); if (errField === 'deadline') setErrField(null); }}
              style={{ ...inputStyle, ...(errField === 'deadline' ? { borderColor: '#e74c3c' } : null) }} />
            {errField === 'deadline' && fieldMsg && <div style={{ fontSize: 'var(--fs-small)', color: '#e74c3c', marginTop: 4 }}>{fieldMsg}</div>}
          </div>
          <div style={{ minWidth: 0 }}>
            <Input id="cf-max" label={tr('campaign.max', 'Макс. инфлюенсеров *')} value={maxCreators} inputMode="numeric" onChange={(e) => { setMaxCreators(e.target.value); if (errField === 'max') setErrField(null); }} error={errField === 'max' ? fieldMsg : null} aria-invalid={errField === 'max' || undefined} />
          </div>
        </div>

        <div>
          {label(tr('campaign.description', 'Описание'))}
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder={tr('campaign.descPh', 'О чём задание, что важно показать…') as string} />
        </div>
        <div>
          {label(tr('campaign.requirements', 'Требования и условия'))}
          <textarea value={requirements} onChange={(e) => setRequirements(e.target.value)} rows={4} style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }} placeholder={tr('campaign.reqPh', 'Что нужно снять (например: 1 Reels + 1–2 Stories с отметкой), сроки, что получает блогер…') as string} />
        </div>

        {err && <div style={{ color: '#e74c3c', fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => save(false)} loading={busy} disabled={uploading}>{tr('campaign.saveDraft', 'Сохранить черновик')}</Button>
          <Button variant="primary" onClick={() => save(true)} loading={busy} disabled={uploading}>{tr('campaign.publish', 'Опубликовать')}</Button>
        </div>
      </div>
    </div>
  );
}
